// src/services/taskService.ts
import { api } from './api';
import { Task, CreateTaskDto, UpdateTaskDto, TaskFilters } from 'types/task';
import { TaskAudit } from 'types/rbac';

export interface TasksResponse {
  tasks: Task[];
  total: number;
  page: number;
  pages: number;
}

export const taskService = {
  getAll: async (filters: TaskFilters = {}): Promise<TasksResponse> => {
    const { data } = await api.get('/tasks', { params: filters });
    return data;
  },

  getOne: async (id: string): Promise<Task> => {
    const { data } = await api.get(`/tasks/${id}`);
    return data;
  },

  // Returns one task per recipient — an array even when assignedTo is a single id.
  create: async (dto: CreateTaskDto): Promise<Task[]> => {
    const { data } = await api.post('/tasks', dto);
    return data;
  },

  update: async (id: string, dto: UpdateTaskDto): Promise<Task> => {
    const { data } = await api.patch(`/tasks/${id}`, dto);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/tasks/${id}`);
  },

  toggleDone: async (id: string): Promise<Task> => {
    const { data } = await api.patch(`/tasks/${id}/done`);
    return data;
  },

  // Drafts are private to the creator until sent — this is what first makes
  // a task visible to the assignee.
  send: async (id: string): Promise<Task> => {
    const { data } = await api.patch(`/tasks/${id}/send`);
    return data;
  },

  // Workflow — Pending -> Accepted -> In Progress -> Review -> Completed
  //                                                 ↘ Rejected/Returned
  accept: async (id: string): Promise<Task> => {
    const { data } = await api.patch(`/tasks/${id}/accept`);
    return data;
  },

  updateProgress: async (id: string, dto: { percent: number; note?: string }): Promise<Task> => {
    const { data } = await api.patch(`/tasks/${id}/progress`, dto);
    return data;
  },

  submitReview: async (id: string): Promise<Task> => {
    const { data } = await api.patch(`/tasks/${id}/submit-review`);
    return data;
  },

  review: async (id: string, dto: { decision: 'approve' | 'reject' | 'return'; remarks?: string }): Promise<Task> => {
    const { data } = await api.patch(`/tasks/${id}/review`, dto);
    return data;
  },

  // Extended workflow actions
  hold: async (id: string, reason?: string): Promise<Task> => {
    const { data } = await api.patch(`/tasks/${id}/hold`, { reason });
    return data;
  },

  resume: async (id: string): Promise<Task> => {
    const { data } = await api.patch(`/tasks/${id}/resume`);
    return data;
  },

  reopen: async (id: string): Promise<Task> => {
    const { data } = await api.patch(`/tasks/${id}/reopen`);
    return data;
  },

  cancel: async (id: string, reason?: string): Promise<Task> => {
    const { data } = await api.patch(`/tasks/${id}/cancel`, { reason });
    return data;
  },

  close: async (id: string): Promise<Task> => {
    const { data } = await api.patch(`/tasks/${id}/close`);
    return data;
  },

  duplicate: async (id: string): Promise<Task> => {
    const { data } = await api.post(`/tasks/${id}/duplicate`);
    return data;
  },

  manageCc: async (id: string, dto: { add?: string[]; remove?: string[] }): Promise<Task> => {
    const { data } = await api.patch(`/tasks/${id}/cc`, dto);
    return data;
  },

  getAudit: async (id: string): Promise<TaskAudit> => {
    const { data } = await api.get(`/tasks/${id}/audit`);
    return data;
  },

  // Subtasks
  addSubtask: async (taskId: string, title: string): Promise<Task> => {
    const { data } = await api.post(`/tasks/${taskId}/subtasks`, { title });
    return data;
  },

  toggleSubtask: async (taskId: string, subtaskId: string): Promise<Task> => {
    const { data } = await api.patch(`/tasks/${taskId}/subtasks/${subtaskId}/done`);
    return data;
  },

  deleteSubtask: async (taskId: string, subtaskId: string): Promise<Task> => {
    const { data } = await api.delete(`/tasks/${taskId}/subtasks/${subtaskId}`);
    return data;
  },

  // Attachments
  addAttachment: async (
    taskId: string,
    attachment: { name: string; url: string; size: number; mimeType: string; key: string }
  ): Promise<Task> => {
    const { data } = await api.post(`/tasks/${taskId}/attachments`, attachment);
    return data;
  },

  deleteAttachment: async (taskId: string, attachmentId: string): Promise<Task> => {
    const { data } = await api.delete(`/tasks/${taskId}/attachments/${attachmentId}`);
    return data;
  },

  // Version history — uploads a new version of an existing attachment
  // instead of replacing it; the prior version stays visible in history.
  addAttachmentVersion: async (
    taskId: string,
    attachmentId: string,
    attachment: { name: string; url: string; size: number; mimeType: string; key: string }
  ): Promise<Task> => {
    const { data } = await api.post(`/tasks/${taskId}/attachments/${attachmentId}/version`, attachment);
    return data;
  },
};
